import { Link, createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, FlaskConical, Lock } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Input, Textarea } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { schemaText, versionUpdate } from "@/features/prompts/versionForm";

export const Route = createFileRoute("/_authed/prompts/$id/versions/$vid")({
  component: VersionEditorPage,
});

function VersionEditorPage() {
  const { id, vid } = Route.useParams();
  const qc = useQueryClient();

  const versionQ = useQuery({
    queryKey: ["admin", "prompt", id, "versions"],
    queryFn: async () => {
      const { data, error } = await api.GET(
        "/api/v1/admin/prompts/{prompt_id}/versions",
        { params: { path: { prompt_id: id } } },
      );
      if (error) throw new Error("failed");
      return data!;
    },
  });

  const llmsQ = useQuery({
    queryKey: ["admin", "llm-options"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/llm-options");
      if (error) throw new Error("failed");
      return data!;
    },
  });

  const version = useMemo(
    () => versionQ.data?.items.find((v) => v.id === vid),
    [versionQ.data, vid],
  );

  // Local form state — initialized from server, kept in sync via effect.
  const [template, setTemplate] = useState("");
  const [llmId, setLlmId] = useState<string>("");
  const [outputSchema, setOutputSchema] = useState("");
  const [temperature, setTemperature] = useState("");
  const [maxTokens, setMaxTokens] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showActivate, setShowActivate] = useState(false);
  const [showTest, setShowTest] = useState(false);

  useEffect(() => {
    if (!version) return;
    setTemplate(version.template);
    setLlmId(version.llm_id ?? "");
    setOutputSchema(schemaText(version.output_schema));
    setTemperature(version.temperature?.toString() ?? "");
    setMaxTokens(version.max_tokens?.toString() ?? "");
    setNotes(version.notes ?? "");
  }, [version]);

  const locked = version?.status !== "draft";

  const save = useMutation({
    mutationFn: async () => {
      const body = versionUpdate({ template, llmId, outputSchema, temperature, maxTokens, notes });

      const { error: apiError } = await api.PATCH(
        "/api/v1/admin/prompts/{prompt_id}/versions/{version_id}",
        {
          params: { path: { prompt_id: id, version_id: vid } },
          body: body as never,
        },
      );
      if (apiError) {
        const detail = (apiError as { detail?: string })?.detail ?? "save failed";
        throw new Error(String(detail));
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "prompt", id, "versions"] });
      qc.invalidateQueries({ queryKey: ["admin", "prompt", id] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : "save failed"),
  });

  const activate = useMutation({
    mutationFn: async () => {
      const { error: apiError } = await api.POST(
        "/api/v1/admin/prompts/{prompt_id}/versions/{version_id}/activate",
        { params: { path: { prompt_id: id, version_id: vid } } },
      );
      if (apiError) {
        const detail =
          (apiError as { detail?: string })?.detail ?? "activate failed";
        throw new Error(String(detail));
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "prompt", id, "versions"] });
      qc.invalidateQueries({ queryKey: ["admin", "prompt", id] });
      qc.invalidateQueries({ queryKey: ["admin", "prompts"] });
      setShowActivate(false);
    },
    onError: (e) => setError(e instanceof Error ? e.message : "activate failed"),
  });

  const canActivate =
    !locked && template.trim() !== "" && llmId !== "";

  if (versionQ.isPending)
    return <p className="text-sm text-zinc-500">Loading…</p>;
  if (!version)
    return <p className="text-sm text-red-600">Version not found.</p>;

  return (
    <div className="space-y-4">
      <Link
        to="/prompts/$id"
        params={{ id }}
        className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900"
      >
        <ChevronLeft className="size-4" />
        Back to prompt
      </Link>

      <Card>
        <CardHeader>
          <CardTitle>
            v{version.version_number}{" "}
            <span className="ml-2 text-xs font-normal text-zinc-500">
              ({version.status})
            </span>
          </CardTitle>
          <div className="flex gap-2">
            {!locked && llmId && template.trim() !== "" && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setShowTest(true)}
              >
                <FlaskConical className="mr-1 size-4" />
                Test
              </Button>
            )}
            {!locked && (
              <Button
                size="sm"
                disabled={!canActivate}
                onClick={() => setShowActivate(true)}
              >
                Activate
              </Button>
            )}
          </div>
        </CardHeader>
        <CardBody className="space-y-5">
          {locked && (
            <div className="flex items-start gap-2 rounded-md border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-700">
              <Lock className="mt-0.5 size-4 shrink-0 text-zinc-500" />
              <p>
                Activated versions are immutable. Use{" "}
                <Link
                  to="/prompts/$id"
                  params={{ id }}
                  className="underline"
                >
                  &quot;New version (from active)&quot;
                </Link>{" "}
                to make changes.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="template">Template (Mustache)</Label>
            <Textarea
              id="template"
              rows={14}
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              disabled={locked}
              placeholder="The Mustache template. Variables interpolate with {{var_name}}."
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="space-y-2 sm:col-span-1">
              <Label htmlFor="llm">LLM</Label>
              <select
                id="llm"
                value={llmId}
                onChange={(e) => setLlmId(e.target.value)}
                disabled={locked}
                className="h-10 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm shadow-sm disabled:bg-zinc-100"
              >
                <option value="" disabled>Choose a model</option>
                {llmsQ.data?.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.code} ({l.provider} · {l.provider_model})
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="temperature">Temperature</Label>
              <Input
                id="temperature"
                type="number"
                step="0.01"
                min="0"
                max="2"
                value={temperature}
                onChange={(e) => setTemperature(e.target.value)}
                disabled={locked}
                placeholder="(default)"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="max_tokens">Max tokens</Label>
              <Input
                id="max_tokens"
                type="number"
                min="1"
                value={maxTokens}
                onChange={(e) => setMaxTokens(e.target.value)}
                disabled={locked}
                placeholder="(default)"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="output_schema">Output schema (JSON Schema)</Label>
            <Textarea
              id="output_schema"
              rows={8}
              value={outputSchema}
              onChange={(e) => setOutputSchema(e.target.value)}
              disabled={locked}
              placeholder='Empty = freeform text. Or e.g. {"type":"object","properties":{...}}'
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={locked}
              placeholder="Why this version differs from prior. Surfaces in audit history."
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          {!locked && (
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setError(null);
                  save.mutate();
                }}
                disabled={save.isPending}
              >
                {save.isPending ? "Saving…" : "Save changes"}
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      {showActivate && (
        <Dialog
          open
          onClose={() => setShowActivate(false)}
          title="Activate this version?"
        >
          <p className="text-sm text-zinc-700">
            The currently-active version (if any) will be deactivated atomically
            and this version becomes live. Activated versions become immutable
            — to make changes later, clone into a new draft.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => setShowActivate(false)}
              disabled={activate.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => activate.mutate()}
              disabled={activate.isPending}
            >
              {activate.isPending ? "Activating…" : "Activate"}
            </Button>
          </div>
        </Dialog>
      )}

      {showTest && (
        <TestVersionDialog
          promptId={id}
          versionId={vid}
          versionNumber={version.version_number}
          onClose={() => setShowTest(false)}
        />
      )}
    </div>
  );
}

function TestVersionDialog({
  promptId,
  versionId,
  versionNumber,
  onClose,
}: {
  promptId: string;
  versionId: string;
  versionNumber: number;
  onClose: () => void;
}) {
  const [variables, setVariables] = useState("{}");
  const [result, setResult] = useState<{
    rendered_template: string;
    output_text: string;
    output_valid: boolean;
    output_validation_error: string | null;
    input_tokens: number;
    output_tokens: number;
    latency_ms: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: async () => {
      let parsedVars: Record<string, unknown>;
      try {
        parsedVars = JSON.parse(variables);
      } catch (e) {
        throw new Error(
          `variables not valid JSON: ${e instanceof Error ? e.message : "?"}`,
        );
      }
      const { data, error: apiError } = await api.POST(
        "/api/v1/admin/prompts/{prompt_id}/versions/{version_id}/test",
        {
          params: { path: { prompt_id: promptId, version_id: versionId } },
          body: { variables: parsedVars },
        },
      );
      if (apiError) {
        const detail = (apiError as { detail?: string })?.detail ?? "run failed";
        throw new Error(String(detail));
      }
      setResult(data!);
    },
    onError: (e) => setError(e instanceof Error ? e.message : "run failed"),
  });

  return (
    <Dialog open onClose={onClose} title={`Test v${versionNumber}`}>
      <div className="space-y-3">
        <div className="space-y-2">
          <Label htmlFor="vars">Variables (JSON)</Label>
          <Textarea
            id="vars"
            rows={4}
            value={variables}
            onChange={(e) => setVariables(e.target.value)}
          />
        </div>
        <div>
          <Button
            onClick={() => {
              setError(null);
              run.mutate();
            }}
            disabled={run.isPending}
          >
            {run.isPending ? "Running…" : "Run against LLM"}
          </Button>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        {result && (
          <div className="space-y-3 border-t border-zinc-200 pt-3">
            <div>
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-zinc-500">
                Rendered prompt
              </div>
              <pre className="max-h-48 overflow-auto rounded bg-zinc-50 p-3 font-mono text-xs whitespace-pre-wrap">
                {result.rendered_template}
              </pre>
            </div>
            <div>
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-zinc-500">
                Output
              </div>
              <pre className="max-h-72 overflow-auto rounded bg-zinc-50 p-3 font-mono text-xs whitespace-pre-wrap">
                {result.output_text}
              </pre>
            </div>
            <div className="flex flex-wrap gap-3 text-xs text-zinc-600">
              <span>
                schema:{" "}
                <span
                  className={
                    result.output_valid
                      ? "text-green-700 font-medium"
                      : "text-red-700 font-medium"
                  }
                >
                  {result.output_valid ? "valid" : "invalid"}
                </span>
              </span>
              <span>in {result.input_tokens} tok</span>
              <span>out {result.output_tokens} tok</span>
              <span>{result.latency_ms} ms</span>
            </div>
            {result.output_validation_error && (
              <p className="text-xs text-red-600">
                {result.output_validation_error}
              </p>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
}
